// src/pages/Home.jsx
// ✅ Última actualización: 2026-02-09 - Simplificación radical a 2 scrolls (Fase Validación)
// HOME = Propósito + Exploración, no venta de producto
// Scroll 1: WHY (Propósito) | Scroll 2: HOW (Cómo trabajamos)
// Eliminado: Catálogo, resultados, metodologías detalladas, promesas de producto

import { useEffect } from 'react';
import { BookOpen, Headphones, FlaskConical, LogIn, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import { Link } from 'react-router-dom';
import '../index.css';
import { useAnalytics } from '../components/Analytics';

export default function Home() {
  const { trackButtonClick } = useAnalytics();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overscrollBehavior = 'none';
    
    return () => {
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  // 🆕 FASE 7: Organization Schema para SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Edutech Pulse Lab",
    "alternateName": "EPL",
    "url": "https://edutechpulselab.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://edutechpulselab.com/isotipoedutech.png",
      "width": 512,
      "height": 512
    },
    "description": "Plataforma de entrenamiento ejecutivo con inteligencia artificial. Simuladores de decisión y sparring con agentes IA especializados para desarrollar criterio, liderazgo y respuesta bajo presión en escenarios reales.",
    "founder": {
      "@type": "Person",
      "name": "David Prudencio",
      "jobTitle": "Director de Innovación Educativa"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "informes@edutech.pe"
    }
  };

  return (
    <>
      <SEOHead 
        title="Entrena Decisiones con IA | Simuladores y Sparring Ejecutivo | Edutech Pulse Lab"
        description="Simuladores de decisión con IA para entrenar criterio, liderazgo y respuesta bajo presión. Enfrenta escenarios reales, recibe feedback inmediato y mejora tu desempeño ejecutivo."
        keywords="entrenamiento ejecutivo IA, simuladores decisión, sparring ejecutivo, práctica liderazgo, feedback IA, escenarios reales, agentes IA, toma decisiones, desarrollo criterio"
        schema={organizationSchema}
      />
      <Navbar />
      <main className="bg-white">

        {/* SCROLL 1: PROPÓSITO (WHY) */}
        <section aria-label="Propósito del laboratorio" className="py-10 sm:py-16 lg:py-20 bg-gradient-to-br from-[#0c203b] via-[#1a365d] to-[#2d4a6b] text-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-left animate-fade-in">
                {/* Badge - Solo visible en desktop */}
                <div className="hidden lg:inline-flex items-center mb-8 sm:mb-10">
                  <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full mr-4 flex-shrink-0"></span>
                  <span className="text-xs font-semibold text-white/80 tracking-widest uppercase">Entrenamiento Ejecutivo con IA</span>
                </div>
                
                {/* Headline centrado en producto */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 leading-[1.15] sm:leading-[1.1] tracking-[-0.01em] sm:tracking-[-0.02em]">
                  Entrena criterio ejecutivo con sparrings de IA
                </h1>
                
                {/* Subtexto con verbos de acción */}
                <p className="text-base sm:text-lg md:text-xl lg:text-xl text-blue-100/85 mb-4 sm:mb-6 leading-relaxed font-light max-w-2xl tracking-wide">
                  Practica conversaciones difíciles, decisiones bajo presión y escenarios reales antes de enfrentarlos en el mundo real.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl lg:text-xl text-blue-100/85 mb-4 sm:mb-6 leading-relaxed font-light max-w-2xl tracking-wide">
                  Simula clientes exigentes, comités de decisión, objeciones complejas y situaciones de liderazgo. Recibe feedback estratégico para mejorar cómo piensas, argumentas y respondes.
                </p>
                
                {/* Diferencial de producto - subido al hero */}
                <p className="text-sm sm:text-base text-blue-200/90 mb-4 font-medium">
                  Sparrings IA especializados · Simuladores de decisión · Feedback estratégico aplicado
                </p>
                
                <p className="text-base sm:text-lg text-blue-200/80 mb-8 sm:mb-10 font-light">
                  La IA es el vehículo. El objetivo es desarrollar mejor criterio humano.
                </p>
                
                {/* CTAs: primario + secundario + link terciario */}
                <div className="flex flex-col gap-3 mb-10 sm:mb-16">
                  {/* Fila 1: dos botones del mismo ancho en mobile, auto en desktop */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/simulador-b2b"
                      className="flex-1 sm:flex-none inline-flex items-center justify-center bg-white text-[#0c203b] px-8 py-3.5 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300"
                      onClick={() => trackButtonClick('Hero - Probar sparring B2B')}
                    >
                      Probar sparring B2B
                    </Link>
                    <Link
                      to="/login"
                      className="flex-1 sm:flex-none inline-flex items-center justify-center border-2 border-white/40 text-white px-8 py-3.5 rounded-xl font-semibold text-base hover:bg-white/10 hover:border-white/60 transition-all duration-300"
                      onClick={() => trackButtonClick('Hero - Ingresar al laboratorio')}
                    >
                      Ingresar al laboratorio
                    </Link>
                  </div>
                  {/* Fila 2: link terciario centrado en mobile, alineado a la izquierda en desktop */}
                  <div className="flex justify-center sm:justify-start">
                    <Link
                      to="/programas"
                      className="inline-flex items-center gap-1.5 text-white/60 hover:text-white/90 text-sm transition-colors duration-200"
                      onClick={() => trackButtonClick('Hero - Ver briefings ejecutivos')}
                    >
                      Ver briefings ejecutivos
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Imagen lado derecho */}
              <div className="relative flex justify-center">
                <div className="relative bg-gray-800 rounded-3xl overflow-hidden shadow-2xl w-full max-w-lg">
                  <img
                    src="/assets/placeholder-executive-video.jpg"
                    alt="Simulador IA en acción - Laboratorio de investigación educativa"
                    className="w-full h-96 lg:h-[480px] object-cover"
                    loading="eager"
                    width="600"
                    height="480"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button 
                      className="w-20 h-20 bg-white bg-opacity-95 rounded-full flex items-center justify-center shadow-2xl cursor-pointer hover:scale-110 transition-transform duration-300 hover:bg-opacity-100"
                      aria-label="Reproducir video demo"
                    >
                      <div className="w-0 h-0 border-l-10 border-l-[#0c203b] border-y-8 border-y-transparent ml-1"></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SCROLL 2: QUÉ ENTRENAS EN EPL */}
        <section aria-label="Qué entrenas en EPL" className="py-14 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">

            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0c203b] leading-tight mb-3">
                Qué entrenas en EPL
              </h2>
            </div>

            {/* Tres bloques de entrenamiento */}
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0c203b] mb-3 sm:mb-4">
                  Criterio bajo presión
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  Aprende a decidir cuando la información es incompleta, el tiempo es limitado y las consecuencias importan.
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0c203b] mb-3 sm:mb-4">
                  Argumentación ejecutiva
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  Practica cómo defender ideas, responder objeciones y construir narrativas sólidas frente a otros decisores.
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0c203b] mb-3 sm:mb-4">
                  Conversaciones difíciles
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  Entrena escenarios con clientes, sponsors, equipos internos o comités que no se convencen fácilmente.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* SECCIÓN: CÓMO FUNCIONA EL ENTRENAMIENTO */}
        <section aria-label="Cómo funciona el entrenamiento" className="py-14 sm:py-20 lg:py-24 bg-[#F8FAFB]">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">

            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0c203b] leading-tight">
                Cómo funciona el entrenamiento
              </h2>
            </div>

            {/* Pasos — manifesto layout */}
            <ol className="space-y-0 divide-y divide-slate-200">
              {[
                {
                  n: '01',
                  title: 'Elige un escenario',
                  text: 'Negociación B2B, innovación, liderazgo, crisis o toma de decisiones.'
                },
                {
                  n: '02',
                  title: 'Conversa con un sparring IA',
                  text: 'La IA actúa como contraparte exigente, no como asistente complaciente.'
                },
                {
                  n: '03',
                  title: 'Recibe feedback estratégico',
                  text: 'Evalúa claridad, criterio, riesgos no vistos, manejo de presión y calidad de respuesta.'
                },
                {
                  n: '04',
                  title: 'Repite con mayor dificultad',
                  text: 'El aprendizaje ocurre cuando vuelves a enfrentar el escenario con mejor criterio.'
                }
              ].map(({ n, title, text }) => (
                <li key={n} className="flex items-start gap-6 py-7 sm:py-8 group">
                  <span className="text-base font-bold text-slate-300 tabular-nums pt-0.5 w-8 flex-shrink-0 group-hover:text-emerald-400 transition-colors">
                    {n}
                  </span>
                  <div className="flex-1">
                    <p className="text-xl sm:text-2xl font-bold text-[#0c203b] leading-snug mb-2">
                      {title}
                    </p>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                      {text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10 sm:mt-12 flex justify-center">
              <a
                href="mailto:informes@edutech.pe?subject=Consulta%20sobre%20Edutech%20Pulse%20Lab"
                className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-6 sm:px-8 py-3 rounded-xl font-medium text-base hover:border-[#0c203b] hover:text-[#0c203b] transition-all duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Escribir al laboratorio
              </a>
            </div>

          </div>
        </section>

        {/* SECCIÓN ACCESO - Prueba una experiencia gratuita */}
        <section aria-label="Prueba una experiencia gratuita" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">

            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0c203b] mb-3 leading-tight">
                Prueba una experiencia gratuita
              </h2>
            </div>

            {/* Grid de 3 experiencias */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              
              {/* Card 1: Sparring B2B */}
              <div className="bg-slate-50 rounded-2xl p-6 sm:p-7 flex flex-col hover:shadow-lg transition-shadow border border-slate-100">
                <h3 className="text-xl font-bold text-[#0c203b] mb-3">
                  Sparring B2B
                </h3>
                <p className="text-base text-slate-600 leading-relaxed mb-6 flex-1">
                  Practica una negociación con un comité comprador que no está alineado.
                </p>
                <Link
                  to="/simulador-b2b"
                  onClick={() => trackButtonClick('Home - Probar sparring')}
                  className="inline-flex items-center justify-center bg-[#1E3A8A] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#17316F] transition-colors"
                >
                  Probar sparring
                </Link>
              </div>

              {/* Card 2: Simulador Lean Startup */}
              <div className="bg-slate-50 rounded-2xl p-6 sm:p-7 flex flex-col hover:shadow-lg transition-shadow border border-slate-100">
                <h3 className="text-xl font-bold text-[#0c203b] mb-3">
                  Simulador Lean Startup
                </h3>
                <p className="text-base text-slate-600 leading-relaxed mb-6 flex-1">
                  Entrena cómo validar una hipótesis antes de construir una solución.
                </p>
                <Link
                  to="/recursos/lean-simulador"
                  onClick={() => trackButtonClick('Home - Validar una idea')}
                  className="inline-flex items-center justify-center bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-cyan-700 transition-colors"
                >
                  Validar una idea
                </Link>
              </div>

              {/* Card 3: Briefings ejecutivos */}
              <div className="bg-slate-50 rounded-2xl p-6 sm:p-7 flex flex-col hover:shadow-lg transition-shadow border border-slate-100">
                <h3 className="text-xl font-bold text-[#0c203b] mb-3">
                  Briefings ejecutivos
                </h3>
                <p className="text-base text-slate-600 leading-relaxed mb-6 flex-1">
                  Audios breves para preparar tu criterio antes de practicar.
                </p>
                <Link
                  to="/programas"
                  onClick={() => trackButtonClick('Home - Ver briefings')}
                  className="inline-flex items-center justify-center bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-violet-700 transition-colors"
                >
                  Ver briefings
                </Link>
              </div>

            </div>

            {/* CTA secundario: acceder al laboratorio completo */}
            <div className="bg-gradient-to-br from-[#0c203b] to-[#1a365d] rounded-2xl p-8 sm:p-10 text-center">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-snug">
                Accede al laboratorio completo
              </h3>
              <p className="text-sm sm:text-base text-blue-200/80 leading-relaxed max-w-2xl mx-auto mb-6">
                Cuenta gratuita con sparrings especializados, simuladores de decisión y seguimiento de tu progreso.
              </p>
              <Link
                to="/login"
                onClick={() => trackButtonClick('Home - Ingresar al laboratorio')}
                className="inline-flex items-center gap-2 bg-white text-[#0c203b] px-8 py-3.5 rounded-xl font-semibold text-base hover:bg-blue-50 transition-colors shadow-lg"
              >
                <LogIn className="w-4 h-4" />
                Ingresar al laboratorio
              </Link>
            </div>

          </div>
        </section>

      </main>

      {/* FOOTER MINIMALISTA */}
      <footer className="bg-[#0a1929] py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2024 Edutech Pulse Lab™
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Laboratorio de investigación en aprendizaje asistido por IA
          </p>
        </div>
      </footer>
      
      <style>
        {`
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          .animate-fade-in {
            animation: fade-in 0.6s ease-out both;
          }
        `}
      </style>
    </>
  );
}
